<script setup lang="ts">
import type { BreadcrumbItem } from './Breadcrumbs.vue'
import Breadcrumbs from './Breadcrumbs.vue'
import PageHeaderIllustration from '@/components/PageHeaderIllustration.vue'

defineProps<{
  title: string
  description?: string
  breadcrumbs?: BreadcrumbItem[]
}>()
</script>

<template>
  <main class="page">
    <div class="page__hero">
      <div class="page__hero-grid" aria-hidden="true"></div>
      <div class="container page__hero-inner">
        <Breadcrumbs v-if="breadcrumbs" :items="breadcrumbs" />
        <header class="page__header">
          <div class="page__text">
            <h1 class="page__title">{{ title }}</h1>
            <p v-if="description" class="page__description">{{ description }}</p>
          </div>
          <div class="page__art" aria-hidden="true">
            <PageHeaderIllustration />
          </div>
        </header>
      </div>
    </div>
    <div class="container page__body">
      <slot />
    </div>
  </main>
</template>

<style scoped>
.page {
  flex: 1;
  padding-bottom: var(--space-12);
}

.page__hero {
  position: relative;
  padding: var(--space-6) 0 var(--space-8);
  background:
    radial-gradient(circle at 80% 10%, rgb(217 119 6 / 0.06), transparent 35%),
    radial-gradient(circle at 10% 90%, rgb(10 92 86 / 0.06), transparent 35%), var(--color-surface);
  border-bottom: 1px solid var(--color-border);
  overflow: hidden;
}

.page__hero-grid {
  position: absolute;
  inset: 0;
  opacity: 0.35;
  background-image: radial-gradient(var(--color-border-strong) 1px, transparent 1px);
  background-size: 28px 28px;
  mask-image: linear-gradient(to bottom, black 20%, transparent 100%);
  -webkit-mask-image: linear-gradient(to bottom, black 20%, transparent 100%);
}

.page__hero-inner {
  position: relative;
}

.page__header {
  position: relative;
  margin-top: var(--space-4);
  display: grid;
  gap: var(--space-6);
  align-items: center;
}

@media (min-width: 768px) {
  .page__header {
    grid-template-columns: 1fr auto;
  }
}

.page__title {
  font-size: var(--text-2xl);
  font-weight: var(--font-extrabold);
  line-height: var(--leading-tight);
  letter-spacing: -0.03em;
  color: var(--color-text);
}

.page__description {
  margin-top: var(--space-3);
  font-size: var(--text-lg);
  color: var(--color-text-muted);
  line-height: var(--leading-relaxed);
  max-width: 55ch;
}

.page__art {
  display: none;
  align-items: center;
  justify-content: flex-end;
}

@media (min-width: 768px) {
  .page__art {
    display: flex;
  }
}

.page__body {
  padding-top: var(--space-8);
}

@media (min-width: 768px) {
  .page__title {
    font-size: var(--text-4xl);
  }

  .page__hero {
    padding-bottom: var(--space-10);
  }
}
</style>
